import Button from "atoms/buttons/button";
import { ButtonStyles } from "atoms/constants/button-styles";
import Trans from "atoms/i18n/trans";
import Loader from "atoms/loaders/loader";
import Heading from "atoms/typography/heading";
import Paragraph from "atoms/typography/paragraph";
import PublicationRecord from "models/view-models/publication-record";
import UserPublicationPinRecord from "models/view-models/user-publication-pin-record";
import PublicationItem, {
    PublicationTextStyle,
} from "organisms/publication-lists/publication-item";
import PublicationListSearch from "organisms/publication-lists/publication-list-search";
import { NotebookReferencePanelTabPanelProps } from "pages/notebooks/notebook-reference-panel/notebook-reference-panel";
import React, { useCallback, useMemo } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import useCurrentNotebook from "utilities/hooks/domain/notebooks/use-current-notebook";
import usePublicationPins from "utilities/hooks/domain/user-publication-pins/use-publication-pins";
import useModalActions from "utilities/hooks/use-modal-actions";
import { t } from "utilities/localization-utils";

interface ReferencesFromPublicationListProps
    extends NotebookReferencePanelTabPanelProps {
    onPublicationClick: (code: string, edition: string) => void;
}

const CSS_CLASS_NAME = "c-notebook-reference-panel";

const ReferencesFromPublicationList = (
    props: ReferencesFromPublicationListProps
) => {
    const { onPublicationClick } = props;
    const { isSharedNotebook } = useCurrentNotebook();
    const { isOpen, handleClose, handleOpen } = useModalActions();
    const handlePublicationClick = (publication: PublicationRecord) => {
        onPublicationClick(publication.code!, publication.edition!);
    };
    const {
        pins,
        setPins,
        refresh: refreshPins,
        loading,
    } = usePublicationPins();

    const pinnedPublications = useMemo(
        () => pins.map((upp) => upp.publication),
        [pins]
    );

    const setAndRefreshPins = useCallback(
        (pins: Array<UserPublicationPinRecord>): void => {
            setPins(pins);
            refreshPins();
        },
        [refreshPins, setPins]
    );

    const handleSearchButtonOpenClick = (
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        e.stopPropagation();
        handleOpen();
    };

    return (
        <div className={`${CSS_CLASS_NAME}__publicationTabContent`}>
            <PublicationListSearch
                pins={pins}
                onChangePins={setAndRefreshPins}
                isOpen={isOpen}
                handleOpen={handleOpen}
                handleClose={handleClose}
                onPublicationClick={handlePublicationClick}
                includeLegacy={false}
            />
            {!isSharedNotebook && (
                <>
                    <Heading cssClassName="c-label -small">
                        {t("pinnedPublications")}
                    </Heading>
                    <div>
                        {loading && (
                            <div className={`${CSS_CLASS_NAME}__loaderWrapper`}>
                                <Loader />
                            </div>
                        )}
                        {CollectionUtils.hasValues(pinnedPublications) ? (
                            pinnedPublications.map((publication) => {
                                return (
                                    <PublicationItem
                                        key={publication.id}
                                        publication={publication}
                                        style={PublicationTextStyle.TitleFirst}
                                        pins={pins}
                                        onChangePins={setAndRefreshPins}
                                        onPublicationClick={
                                            handlePublicationClick
                                        }
                                    />
                                );
                            })
                        ) : (
                            <div className={`${CSS_CLASS_NAME}__pins`}>
                                <Paragraph
                                    cssClassName={`${CSS_CLASS_NAME}__noPinsMessage`}>
                                    <Trans i18nKey="pinned-publications--no-pins-message">
                                        Publications pinned to this notebook
                                        will show up here. Start by
                                        <Button
                                            cssClassName={`${CSS_CLASS_NAME}__searchButton`}
                                            onClick={
                                                handleSearchButtonOpenClick
                                            }
                                            style={ButtonStyles.AnchorBlue}>
                                            searching the publication
                                        </Button>
                                        .
                                    </Trans>
                                </Paragraph>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ReferencesFromPublicationList;
